import intl from "react-intl-universal";
import { entityDocumentHasUploadedContent } from "../../../../Services/baasEntidadesApi";
import {
  extractDocumentCountryCode,
  formatIdentityDocumentLabel,
  resolveIdentityDocumentLabelVariant,
} from "./baasIdentityDocumentLabels";
import {
  getDeclarationPickerId,
  IDENTITY_VARIANT,
  nationalIdNumberIsFilled,
  normalizeSchemeCountry,
  shouldHidePassportForNationalId,
} from "./baasIdentitySchemes";

export const BASE64_PREVIEW_CHARS = 240;

export const DOCUMENT_TYPE_LABEL_KEYS = {
  PASSPORT: "baas.entidades.create.identity.passport",
  DRIVERS_LICENSE: "baas.entidades.create.identity.driversLicense",
  DRIVERS_LICENSE_BACK: "baas.entidades.detail.docType.driversLicenseBack",
  NATIONAL_ID: "baas.entidades.create.identity.nationalId",
  SSN: "baas.entidades.detail.docType.ssn",
  SELFIE: "baas.entidades.detail.docType.selfie",
  PROOF_OF_RESIDENCE: "baas.entidades.detail.docType.proofOfResidence",
  REGISTRATION_ID: "baas.entidades.detail.docType.registrationId",
  EIN: "baas.entidades.detail.docType.ein",
  ARTICLES_OF_INCORPORATION: "baas.entidades.detail.docType.articles",
  PROOF_OF_BUSINESS_ADDRESS: "baas.entidades.detail.docType.businessAddress",
  FINANCIAL_STATEMENT: "baas.entidades.detail.docType.financialStatement",
};

export const DECLARATION_DOCUMENT_TYPES = new Set([
  "PASSPORT",
  "DRIVERS_LICENSE",
  "NATIONAL_ID",
  "SSN",
  "REGISTRATION_ID",
  "EIN",
  "ARTICLES_OF_INCORPORATION",
  "PROOF_OF_BUSINESS_ADDRESS",
  "FINANCIAL_STATEMENT",
]);

export const PERSON_ONLY_DOCUMENT_TYPES = new Set([
  "PASSPORT",
  "DRIVERS_LICENSE",
  "DRIVERS_LICENSE_BACK",
  "NATIONAL_ID",
  "SELFIE",
  "SSN",
  "PROOF_OF_RESIDENCE",
]);

export const BUSINESS_UPLOAD_TYPES = [
  { value: "REGISTRATION_ID", labelKey: "baas.entidades.detail.docType.registrationId" },
  { value: "EIN", labelKey: "baas.entidades.detail.docType.ein" },
  { value: "ARTICLES_OF_INCORPORATION", labelKey: "baas.entidades.detail.docType.articles" },
  { value: "PROOF_OF_BUSINESS_ADDRESS", labelKey: "baas.entidades.detail.docType.businessAddress" },
  { value: "FINANCIAL_STATEMENT", labelKey: "baas.entidades.detail.docType.financialStatement" },
];

export const BUSINESS_DECLARATION_ACCORDION_TYPES = [
  { value: "REGISTRATION_ID", labelKey: "baas.entidades.detail.docType.registrationId" },
  { value: "EIN", labelKey: "baas.entidades.detail.docType.ein" },
];

export const getDocumentTypeKey = (doc) =>
  String(doc?.document_id || doc?.documentId || doc?.description || "").toUpperCase();

export const hasUploadedContent = (doc) => entityDocumentHasUploadedContent(doc);

export const isDeclarationDocument = (doc) => {
  const type = getDocumentTypeKey(doc);
  if (DECLARATION_DOCUMENT_TYPES.has(type)) return true;
  return Boolean(
    doc?.number
      || doc?.country_code
      || doc?.countryCode
      || doc?.issuing_region
      || doc?.issuingRegion
      || doc?.expiration_date
      || doc?.expirationDate,
  );
};

export const emptyDeclarationDoc = (type, countryCode = "") => ({
  document_id: type,
  number: "",
  country_code: countryCode,
  issuing_region: "",
  expiration_date: "",
});

export const formatDocumentDate = (value) => {
  if (!value) return null;
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: "short" }).format(new Date(value));
  } catch {
    return String(value);
  }
};

export const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

export const resolveUploadContentType = (file) => {
  const type = String(file?.type || "")
    .trim()
    .toLowerCase();
  if (type && type !== "application/octet-stream") return type;
  const name = String(file?.name || "").toLowerCase();
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".webp")) return "image/webp";
  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg") || name.endsWith(".jpe")) {
    return "image/jpeg";
  }
  return type || "image/jpeg";
};

export const extractRawBase64 = (value) => {
  if (!value) return null;
  const str = String(value);
  if (str.startsWith("data:")) {
    const comma = str.indexOf(",");
    return comma >= 0 ? str.slice(comma + 1) : null;
  }
  if (str.startsWith("blob:") || /^https?:\/\//i.test(str)) return null;
  return str;
};

export const fetchUrlAsBase64 = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch document (${response.status})`);
  }
  const blob = await response.blob();
  return readFileAsBase64(blob);
};

export const formatDocumentTypeLabel = (documentType, options = {}) => {
  const type = String(documentType || "").toUpperCase();
  const fallbackKey = DOCUMENT_TYPE_LABEL_KEYS[type];
  const label = formatIdentityDocumentLabel({
    documentType: type,
    countryCode: options.countryCode,
    countryOptions: options.countryOptions,
    variant: options.variant,
    labelRole: options.labelRole,
    identityVariant: options.identityVariant,
    doc: options.doc,
  });
  if (label && label !== type) return label;
  if (fallbackKey && intl.get(fallbackKey)) return intl.get(fallbackKey);
  return documentType || "—";
};

export const toPreviewSrc = (fileBase64, contentType) => {
  if (!fileBase64) return null;
  if (fileBase64.startsWith("data:") || fileBase64.startsWith("blob:") || /^https?:\/\//i.test(fileBase64)) {
    return fileBase64;
  }
  const mime = contentType || "application/octet-stream";
  return `data:${mime};base64,${fileBase64}`;
};

export const isPdfPreview = (contentType, src) => {
  const mime = String(contentType || "").toLowerCase();
  if (mime.includes("pdf")) return true;
  if (typeof src === "string" && src.toLowerCase().includes(".pdf")) return true;
  return false;
};

export const isImagePreview = (contentType, src) => {
  const mime = String(contentType || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  if (typeof src === "string") {
    if (/^data:image\//i.test(src)) return true;
    if (/\.(jpe?g|png|gif|webp|bmp|svg)(\?|#|$)/i.test(src)) return true;
  }
  return false;
};

const docHasDeclarationData = (doc) =>
  Boolean(
    doc?.number
      || doc?.id
      || extractDocumentCountryCode(doc)
      || doc?.expiration_date
      || doc?.expirationDate,
  );

export const inferNationalIdIdentityVariant = (nid, uploadedDocs = []) => {
  const cc = extractDocumentCountryCode(nid);
  if (cc === "US") return IDENTITY_VARIANT.ITIN;

  const hasCnhProof = uploadedDocs.some((item) => {
    if (getDocumentTypeKey(item) !== "DRIVERS_LICENSE") return false;
    const dlCountry = extractDocumentCountryCode(item);
    return !dlCountry || dlCountry === "BR";
  });
  const hasNidImage = uploadedDocs.some(
    (item) => getDocumentTypeKey(item) === "NATIONAL_ID" && hasUploadedContent(item),
  );
  if (hasNidImage && !hasCnhProof) return IDENTITY_VARIANT.RG;
  return IDENTITY_VARIANT.CPF;
};

export const inferRegistrationIdIdentityVariant = () => IDENTITY_VARIANT.CRM;

export const buildExistingDeclarationPickerIds = (slots = []) => {
  const ids = new Set();
  let hasNationalId = false;

  for (const slot of slots) {
    const type = String(slot?.type || "").toUpperCase();
    if (!type) continue;
    if (type === "NATIONAL_ID") {
      hasNationalId = true;
      ids.add(getDeclarationPickerId(type, slot.identityVariant || inferNationalIdIdentityVariant(slot.doc)));
      continue;
    }
    if (type === "REGISTRATION_ID") {
      ids.add(getDeclarationPickerId(type, slot.identityVariant || IDENTITY_VARIANT.CRM));
      continue;
    }
    ids.add(type);
  }

  if (hasNationalId) {
    ids.add(getDeclarationPickerId("NATIONAL_ID", IDENTITY_VARIANT.CPF));
    ids.add(getDeclarationPickerId("NATIONAL_ID", IDENTITY_VARIANT.RG));
    ids.add(getDeclarationPickerId("NATIONAL_ID", IDENTITY_VARIANT.ITIN));
  }

  return [...ids];
};

const docHasUploadedProof = (doc) => hasUploadedContent(doc);

export const isBrCpfProofDeclaration = (documentType, identityVariant, countryCode) => {
  const type = String(documentType || "").toUpperCase();
  const variant = String(identityVariant || "").trim().toLowerCase();
  const cc = normalizeSchemeCountry(countryCode);
  if (type !== "NATIONAL_ID") return false;
  if (variant && variant !== IDENTITY_VARIANT.CPF) return false;
  return !cc || cc === "BR";
};

export const looksLikeBrazilianCnhFile = (doc) => {
  const fileName = String(doc?.file_name || doc?.fileName || doc?.FileName || "").toLowerCase();
  if (!fileName) return false;
  return fileName.includes("_cnh_");
};

export const resolveDriversLicenseCountry = (dl, uploadedDocs = [], allDocs = []) => {
  const dlCandidates = [
    dl,
    ...uploadedDocs.filter(
      (item) => ["DRIVERS_LICENSE", "DRIVERS_LICENSE_BACK"].includes(getDocumentTypeKey(item)),
    ),
  ].filter(Boolean);

  if (dlCandidates.some(looksLikeBrazilianCnhFile)) {
    return "BR";
  }

  const fromDeclaration = extractDocumentCountryCode(dl);
  if (fromDeclaration) return fromDeclaration;

  const dlUpload = uploadedDocs.find(
    (item) => getDocumentTypeKey(item) === "DRIVERS_LICENSE",
  );
  if (dlUpload) {
    const own = extractDocumentCountryCode(dlUpload);
    if (own) return own;
    return resolveDocumentCountryForLabel(dlUpload, allDocs.length ? allDocs : uploadedDocs);
  }

  const dlBackUpload = uploadedDocs.find(
    (item) => getDocumentTypeKey(item) === "DRIVERS_LICENSE_BACK",
  );
  if (dlBackUpload) {
    const own = extractDocumentCountryCode(dlBackUpload);
    if (own) return own;
    return resolveDocumentCountryForLabel(dlBackUpload, allDocs.length ? allDocs : uploadedDocs);
  }

  return "";
};

export const hasForeignDriversLicenseIdentity = (declarationDocs = [], uploadedDocs = []) => {
  const byType = new Map();
  for (const doc of declarationDocs) {
    const type = getDocumentTypeKey(doc);
    if (!type || byType.has(type)) continue;
    byType.set(type, doc);
  }
  const dl = byType.get("DRIVERS_LICENSE");
  const allDocs = [...declarationDocs, ...uploadedDocs];

  const dlCandidates = [
    dl,
    ...uploadedDocs.filter(
      (item) => ["DRIVERS_LICENSE", "DRIVERS_LICENSE_BACK"].includes(getDocumentTypeKey(item)),
    ),
  ].filter(Boolean);

  if (dlCandidates.some((item) => looksLikeBrazilianCnhFile(item))) {
    return false;
  }

  const dlCountry = resolveDriversLicenseCountry(dl, uploadedDocs, allDocs);
  const hasDlProof = uploadedDocs.some(
    (item) =>
      ["DRIVERS_LICENSE", "DRIVERS_LICENSE_BACK"].includes(getDocumentTypeKey(item))
      && hasUploadedContent(item),
  );
  const hasDlDeclaration = Boolean(dl && docHasDeclarationData(dl));

  if (!dlCountry) {
    return false;
  }
  if (dlCountry === "BR") {
    return false;
  }
  return hasDlDeclaration || hasDlProof;
};

/**
 * Person declaration blocks derived from API data — no empty fixed accordions.
 * Foreign DL (e.g. US) suppresses BR CPF slot unless explicitly pending.
 */
export const resolvePersonDeclarationSlots = (
  declarationDocs,
  pendingSlots = [],
  uploadedDocs = [],
) => {
  const byType = new Map();
  for (const doc of declarationDocs) {
    const type = getDocumentTypeKey(doc);
    if (!type || byType.has(type)) continue;
    byType.set(type, doc);
  }

  const slots = [];
  const nid = byType.get("NATIONAL_ID");
  const dl = byType.get("DRIVERS_LICENSE");
  const hasForeignDlPrimary = hasForeignDriversLicenseIdentity(declarationDocs, uploadedDocs);
  const hasNid = nationalIdNumberIsFilled(nid);
  const hasDlData = dl && docHasDeclarationData(dl);
  const isBrCpfPath = hasNid && (!extractDocumentCountryCode(nid) || extractDocumentCountryCode(nid) === "BR");
  const nidCountry = extractDocumentCountryCode(nid);
  const isBrCpfNid = !nidCountry || nidCountry === "BR";
  const suppressBrCpfForForeignDl = hasForeignDlPrimary && isBrCpfNid;

  if (
    !suppressBrCpfForForeignDl
    && (hasNid || (docHasDeclarationData(nid) && !hasForeignDlPrimary))
  ) {
    const identityVariant = inferNationalIdIdentityVariant(nid, uploadedDocs);
    // CPF number is edited on the Personal data tab — not a documents declaration block.
    if (identityVariant !== IDENTITY_VARIANT.CPF) {
      slots.push({
        type: "NATIONAL_ID",
        identityVariant,
        doc: nid || emptyDeclarationDoc("NATIONAL_ID"),
      });
    }
  }

  if (hasDlData) {
    slots.push({ type: "DRIVERS_LICENSE", doc: dl });
  }

  const passport = byType.get("PASSPORT");
  if (passport && docHasDeclarationData(passport) && !shouldHidePassportForNationalId(nid)) {
    slots.push({ type: "PASSPORT", doc: passport });
  }

  for (const [type, doc] of byType) {
    if (["NATIONAL_ID", "DRIVERS_LICENSE", "PASSPORT"].includes(type)) continue;
    if (docHasDeclarationData(doc)) {
      slots.push({
        type,
        identityVariant: type === "REGISTRATION_ID"
          ? inferRegistrationIdIdentityVariant()
          : null,
        doc,
      });
    }
  }

  for (const pending of pendingSlots) {
    if (!slots.some((slot) => {
      if (slot.type !== pending.type) return false;
      if (pending.type === "NATIONAL_ID") {
        return slot.identityVariant === pending.identityVariant;
      }
      return true;
    })) {
      slots.push(pending);
    }
  }

  if (!slots.some((slot) => slot.type === "DRIVERS_LICENSE")) {
    const dlUpload = uploadedDocs.find(
      (item) => getDocumentTypeKey(item) === "DRIVERS_LICENSE",
    );
    const hasCnhProof = uploadedDocs.some(
      (item) =>
        ["DRIVERS_LICENSE", "DRIVERS_LICENSE_BACK"].includes(getDocumentTypeKey(item))
        && hasUploadedContent(item),
    );
    if (hasCnhProof) {
      const dlCountry =
        resolveDriversLicenseCountry(dl, uploadedDocs, [...declarationDocs, ...uploadedDocs])
        || "BR";
      slots.push({
        type: "DRIVERS_LICENSE",
        doc: byType.get("DRIVERS_LICENSE") || {
          ...dlUpload,
          document_id: "DRIVERS_LICENSE",
          country_code: dlCountry,
        },
      });
    }
  }

  return slots;
};

export const buildExistingProofByType = (uploadedDocs) => {
  const map = {};
  for (const doc of uploadedDocs) {
    const type = getDocumentTypeKey(doc);
    if (!type || map[type]) continue;
    map[type] = doc;
  }
  return map;
};

/** PJ company file checklist from API docs. */
export const buildBusinessFileChecklist = (documents) => {
  const apiDocsByType = {};
  if (Array.isArray(documents)) {
    for (const doc of documents) {
      const type = getDocumentTypeKey(doc);
      if (!type || apiDocsByType[type]) continue;
      apiDocsByType[type] = doc;
    }
  }

  const catalogValues = new Set(BUSINESS_UPLOAD_TYPES.map((def) => def.value));
  const items = BUSINESS_UPLOAD_TYPES.map((def) => {
    const apiDoc = apiDocsByType[def.value] || null;
    const hasFile = Boolean(apiDoc && hasUploadedContent(apiDoc));
    return {
      ...def,
      existing: hasFile ? apiDoc : null,
      declared: apiDoc,
      hasFile,
      number: apiDoc?.number || null,
    };
  });

  for (const [type, apiDoc] of Object.entries(apiDocsByType)) {
    if (catalogValues.has(type) || PERSON_ONLY_DOCUMENT_TYPES.has(type)) continue;
    const hasFile = Boolean(apiDoc && hasUploadedContent(apiDoc));
    items.push({
      value: type,
      labelKey: DOCUMENT_TYPE_LABEL_KEYS[type] || null,
      existing: hasFile ? apiDoc : null,
      declared: apiDoc,
      hasFile,
      number: apiDoc?.number || null,
    });
  }
  return items;
};

/** PJ declaration accordion slots (registration, EIN, etc.). */
export const resolveBusinessDeclarationSlots = (declarationDocs) => {
  const byType = new Map();
  for (const doc of declarationDocs) {
    const type = getDocumentTypeKey(doc);
    if (!type || byType.has(type)) continue;
    byType.set(type, doc);
  }

  return BUSINESS_DECLARATION_ACCORDION_TYPES.map((def) => ({
    type: def.value,
    labelKey: def.labelKey,
    doc: byType.get(def.value) || emptyDeclarationDoc(def.value),
  }));
};

/** Infer issuing country when API omits country_code (common on DL back / selfie). */
export const resolveDocumentCountryForLabel = (doc, allDocuments = []) => {
  const own = extractDocumentCountryCode(doc);
  if (own) return own;

  const type = getDocumentTypeKey(doc);
  if (!Array.isArray(allDocuments) || allDocuments.length === 0) return "";

  if (type === "DRIVERS_LICENSE_BACK") {
    const front = allDocuments.find(
      (item) => getDocumentTypeKey(item) === "DRIVERS_LICENSE",
    );
    return extractDocumentCountryCode(front);
  }

  if (type === "SELFIE") {
    const dl = allDocuments.find(
      (item) => getDocumentTypeKey(item) === "DRIVERS_LICENSE",
    );
    const dlCountry = extractDocumentCountryCode(dl);
    if (dlCountry) return dlCountry;
    const nid = allDocuments.find(
      (item) => getDocumentTypeKey(item) === "NATIONAL_ID",
    );
    return extractDocumentCountryCode(nid);
  }

  return "";
};

export const formatStoredFileLabel = (doc, countryOptions, allDocuments = []) => {
  const documentType = doc.document_id || doc.documentId || doc.description;
  const docCountry = resolveDocumentCountryForLabel(doc, allDocuments);
  return formatDocumentTypeLabel(documentType, {
    doc,
    countryCode: docCountry,
    countryOptions,
    variant: resolveIdentityDocumentLabelVariant({
      documentType,
      countryCode: docCountry,
      isLinkedNationalIdProof: Boolean(
        doc.related_person_document_id || doc.relatedPersonDocumentId,
      ),
    }),
  });
};
